/**
 * Currency
 *
 * Utility class for formatting numbers as currency strings.
 * Provides static methods to format values in cents or dollars
 * using locale and currency code.
 *
 * Example usage:
 *   Currency.formatCents(12345); // "$123.45"
 *   Currency.formatDollar(123.45, "en-GB", "GBP"); // "£123.45"
 *
 * If the input is not a valid number, returns an empty string.
 *
 * @class
 */
class Currency {
  static #isValidNumber(value) {
    return typeof value === 'number' && !isNaN(value);
  }

  static #currencyFormatter(locale = 'en-US', currency = 'USD') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    });
  }

  static #formatCurrency(value, locale = 'en-US', currency = 'USD') {
    return this.#currencyFormatter(locale, currency).format(value);
  }

  /**
   * Format a value in cents as a currency string.
   * @param {number} cents - The value in cents (e.g., 12345 for $123.45)
   * @param {string} [locale="en-US"] - BCP 47 locale string (e.g., "en-US")
   * @param {string} [currency="USD"] - ISO 4217 currency code (e.g., "USD")
   * @returns {string} Formatted currency string, or empty string if invalid input
   */
  static formatCents(cents, locale = 'en-US', currency = 'USD') {
    if (!this.#isValidNumber(cents)) return '';
    const dollars = cents / 100;
    return this.#formatCurrency(dollars, locale, currency);
  }

  /**
   * Format a value in dollars as a currency string.
   * @param {number} dollars - The value in dollars (e.g., 123.45)
   * @param {string} [locale="en-US"] - BCP 47 locale string (e.g., "en-US")
   * @param {string} [currency="USD"] - ISO 4217 currency code (e.g., "USD")
   * @returns {string} Formatted currency string, or empty string if invalid input
   */
  static formatDollar(dollars, locale = 'en-US', currency = 'USD') {
    if (!this.#isValidNumber(dollars)) return '';
    return this.#formatCurrency(dollars, locale, currency);
  }
}

export default Currency;
